import React, { forwardRef } from "react";
import { cn } from "../lib/utils.js";
import { mergeRefs } from "./mergeRefs.js";

/* Slot — the engine behind `asChild` (link-button-aschild). It renders its
   one child element in its own place and merges the parent's props onto it,
   so `<Link asChild><RouterLink to="/x" /></Link>` paints the host router's
   own <a> with the DS classes: one anchor, the router's href, no nesting.
   Only single-element atoms (Link, Button) take asChild.

   The merge: the child's own props win, except that
   - className joins through cn(), the child's classes last;
   - style merges, the child's keys last;
   - a handler both define runs the child's first, and the parent's only if
     the child did not call preventDefault();
   - the ref reaches both the parent's forwarded ref and the child's own,
     through mergeRefs; with no forwarded ref the child keeps its own.
   A child prop set to undefined leaves the parent's value in place.

   Exactly one element child: none, several, text or a fragment throws in
   development and renders nothing in production. Engine tier, internal: not
   exported from the barrel. */

type Props = Record<string, unknown>;
type Handler = (...args: unknown[]) => void;

/** An event handler prop: `on` and a capital letter (onClick, onKeyDown). */
const HANDLER = /^on[A-Z]/;

function composeHandlers(child: Handler, slot: Handler): Handler {
  return (...args) => {
    child(...args);
    const event = args[0] as { defaultPrevented?: boolean } | undefined;
    if (!event?.defaultPrevented) slot(...args);
  };
}

function mergeProps(slot: Props, child: Props): Props {
  const merged: Props = { ...slot };
  for (const [key, childValue] of Object.entries(child)) {
    if (childValue === undefined) continue;
    const slotValue = slot[key];
    if (
      HANDLER.test(key) &&
      typeof slotValue === "function" &&
      typeof childValue === "function"
    ) {
      merged[key] = composeHandlers(
        childValue as Handler,
        slotValue as Handler,
      );
    } else if (key === "className") {
      merged[key] = cn(slotValue as string | undefined, childValue as string);
    } else if (key === "style") {
      merged[key] = { ...(slotValue as object), ...(childValue as object) };
    } else {
      merged[key] = childValue;
    }
  }
  return merged;
}

export interface SlotProps extends React.HTMLAttributes<HTMLElement> {
  /** Any other prop (href, target, rel, data-*) reaches the child as is. */
  [prop: string]: unknown;
}

export const Slot = forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, ...slotProps },
  forwardedRef,
) {
  if (
    !React.isValidElement<Props>(children) ||
    children.type === React.Fragment
  ) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error(
        "[Slot] asChild needs exactly one element child (a link element), not none, several, text or a fragment.",
      );
    }
    return null;
  }
  const childRef = (children as { ref?: React.Ref<HTMLElement> }).ref;
  return React.cloneElement(children, {
    ...mergeProps(slotProps, children.props),
    // With no forwarded ref the child keeps its own, so a child that does
    // not forward refs gets none.
    ref: forwardedRef
      ? mergeRefs<HTMLElement>(forwardedRef, childRef)
      : childRef,
  });
});
